/**
 * Bench generator — a straight bench built from box geometry.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 *   - X = width (along the bench length)
 *   - Y = depth (front–back)
 *   - Z = height
 *
 * Local anchor: origin at the geometric centre of the bench footprint at
 * Z = 0 (floor level), matching the convention used by other prop generators.
 *
 * Leg styles:
 *   end_frame — an H-frame at each end: two vertical legs joined by a
 *               horizontal stretcher near the floor. Proportionally clean
 *               for both park benches and weight benches.
 *   four_leg  — four independent corner legs, inset from the seat edge.
 *
 * Optional back: a flat panel rising from the rear of the seat, tilted back
 * by `backAngle`. Support posts connect the back panel to the seat frame.
 *
 * Optional stretcher: a single horizontal rail connecting the two end frames
 * near floor level for extra rigidity (shown only for end_frame style).
 */

import type { BoxSpec } from "./generatorGeom.ts";
import {
  buildBox,
  concatBoxes,
  concatPrimitiveGeometries,
  transformGeometry,
} from "./generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  boolParam,
  defineGenerator,
  enumParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface BenchParams extends GeneratorParams {
  width: number;
  depth: number;
  seatHeight: number;
  seatThickness: number;
  legStyle: string;
  legThickness: number;
  legInset: number;
  hasStretcher: boolean;
  stretcherHeight: number;
  hasBack: boolean;
  backHeight: number;
  backAngle: number;
  backThickness: number;
}

const BENCH_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "bench-seat",
    label: "Seat",
    params: [
      {
        id: "width",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 1.5,
        min: 0.5,
        max: 4.0,
        step: 0.05,
      },
      {
        id: "depth",
        label: "Depth",
        type: "number",
        unit: "meters",
        default: 0.4,
        min: 0.2,
        max: 0.8,
        step: 0.025,
      },
      {
        id: "seatHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.46,
        min: 0.3,
        max: 0.8,
        step: 0.01,
      },
      {
        id: "seatThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.04,
        min: 0.02,
        max: 0.12,
        step: 0.005,
      },
    ],
  },
  {
    type: "section",
    id: "bench-legs",
    label: "Legs",
    params: [
      {
        id: "legStyle",
        label: "Style",
        type: "enum",
        default: "end_frame",
        options: [
          { value: "end_frame", label: "End Frames" },
          { value: "four_leg", label: "Four Legs" },
        ],
      },
      {
        id: "legThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.05,
        min: 0.02,
        max: 0.12,
        step: 0.005,
      },
      {
        id: "legInset",
        label: "Inset",
        type: "number",
        unit: "meters",
        default: 0.05,
        min: 0.0,
        max: 0.2,
        step: 0.005,
        showIf: { id: "legStyle", value: "four_leg" },
      },
      {
        id: "hasStretcher",
        label: "Centre Stretcher",
        type: "boolean",
        default: false,
        showIf: { id: "legStyle", value: "end_frame" },
      },
      {
        id: "stretcherHeight",
        label: "Stretcher Height",
        type: "number",
        unit: "meters",
        default: 0.12,
        min: 0.05,
        max: 0.35,
        step: 0.01,
        showIf: { id: "hasStretcher", value: true },
      },
    ],
  },
  {
    type: "section",
    id: "bench-back",
    label: "Back",
    defaultCollapsed: true,
    params: [
      { id: "hasBack", label: "Back", type: "boolean", default: false },
      {
        id: "backHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.42,
        min: 0.1,
        max: 1.0,
        step: 0.025,
        showIf: { id: "hasBack", value: true },
      },
      {
        id: "backAngle",
        label: "Angle",
        type: "number",
        unit: "degrees",
        default: 8,
        min: 0,
        max: 30,
        step: 1,
        showIf: { id: "hasBack", value: true },
      },
      {
        id: "backThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.04,
        min: 0.02,
        max: 0.1,
        step: 0.005,
        showIf: { id: "hasBack", value: true },
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

function buildBench(
  p: BenchParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const width = numParam(p, "width", 1.5);
  const depth = numParam(p, "depth", 0.4);
  const seatH = numParam(p, "seatHeight", 0.46);
  const seatT = numParam(p, "seatThickness", 0.04);
  const legStyle = enumParam(p, "legStyle", "end_frame");
  const legT = numParam(p, "legThickness", 0.05);
  const legInset = numParam(p, "legInset", 0.05);
  const hasStretch = boolParam(p, "hasStretcher", false);
  const stretchH = numParam(p, "stretcherHeight", 0.12);
  const hasBack = boolParam(p, "hasBack", false);
  const backH = numParam(p, "backHeight", 0.42);
  const backAng = numParam(p, "backAngle", 8) * (Math.PI / 180);
  const backT = numParam(p, "backThickness", 0.04);

  const boxes: BoxSpec[] = [];

  // ── Seat ──────────────────────────────────────────────────────────────────
  // Top face flush with Z = seatH.
  boxes.push({
    cx: 0,
    cy: 0,
    cz: seatH - seatT * 0.5,
    sx: width,
    sy: depth,
    sz: seatT,
  });

  // ── Legs ──────────────────────────────────────────────────────────────────
  const legH = seatH - seatT; // floor to underside of seat
  const legCZ = legH * 0.5;

  if (legStyle === "four_leg") {
    const xOff = width * 0.5 - legInset - legT * 0.5;
    const yOff = depth * 0.5 - legT * 0.5;
    for (const sx of [1, -1]) {
      for (const sy of [1, -1]) {
        boxes.push({
          cx: sx * xOff,
          cy: sy * yOff,
          cz: legCZ,
          sx: legT,
          sy: legT,
          sz: legH,
        });
      }
    }
  } else {
    // End-frame: two vertical legs + a horizontal cross-brace per frame.
    // The frame spans the seat depth; brace sits at stretcherHeight if enabled,
    // otherwise just below mid-leg height for structural appearance.
    const xOff = width * 0.5 - legT * 0.5;
    const yOff = depth * 0.5 - legT * 0.5;
    const braceH = Math.max(0.025, legT * 0.6);
    const braceZ = legH * 0.35; // default decorative placement
    const braceSpan = Math.max(0.01, depth - legT * 2);

    for (const sx of [1, -1]) {
      // Front and rear verticals of the H-frame
      boxes.push({
        cx: sx * xOff,
        cy: yOff,
        cz: legCZ,
        sx: legT,
        sy: legT,
        sz: legH,
      });
      boxes.push({
        cx: sx * xOff,
        cy: -yOff,
        cz: legCZ,
        sx: legT,
        sy: legT,
        sz: legH,
      });
      // Horizontal brace between them
      boxes.push({
        cx: sx * xOff,
        cy: 0,
        cz: braceZ,
        sx: legT,
        sy: braceSpan,
        sz: braceH,
      });
    }

    // Optional centre stretcher — a rail connecting the two end frames
    if (hasStretch) {
      const stretchSpan = Math.max(0.01, width - legT * 2);
      const stretchCZ = Math.min(stretchH, legH - legT);
      boxes.push({
        cx: 0,
        cy: 0,
        cz: stretchCZ,
        sx: stretchSpan,
        sy: legT,
        sz: legT,
      });
    }
  }

  // ── Back ──────────────────────────────────────────────────────────────────
  // The back panel pivots about its bottom edge at the rear of the seat top,
  // exactly as in the chair generator. Built as a separate transformed geometry
  // so the hinge rotation is clean.
  if (hasBack) {
    // Support posts rise from the seat level to the back height at each end,
    // providing a visible connection from the seat frame to the back panel.
    const postSz = Math.max(legT, backT);
    const postH = backH;
    const postX = width * 0.5 - postSz * 0.5;
    // Posts sit just inside the seat ends
    boxes.push({
      cx: postX,
      cy: -(depth * 0.5 - postSz * 0.5),
      cz: seatH + postH * 0.5,
      sx: postSz,
      sy: postSz,
      sz: postH,
    });
    boxes.push({
      cx: -postX,
      cy: -(depth * 0.5 - postSz * 0.5),
      cz: seatH + postH * 0.5,
      sx: postSz,
      sy: postSz,
      sz: postH,
    });

    // Back panel — centred in child-local space at (0, 0, backH/2), then
    // rotated about its bottom edge and translated to the hinge point.
    // Hinge sits at the rear of the seat top: Y = -(depth/2 - backT/2), Z = seatH.
    const panelGeo = buildBox(Math.max(0.01, width - postSz * 2), backT, backH);
    const hingeY = -(depth * 0.5 + backT * 0.5);
    // Shift the panel centre up by backH/2 in the child frame before rotate
    const shifted = transformGeometry(panelGeo, [0, 0, backH * 0.5]);
    const rotated = transformGeometry(
      shifted,
      [0, hingeY, seatH],
      [backAng, 0, 0],
    );

    return {
      kind: "mesh",
      geometry: concatPrimitiveGeometries([concatBoxes(boxes), rotated]),
    };
  }

  return { kind: "mesh", geometry: concatBoxes(boxes) };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const BENCH_GENERATOR: Generator = defineGenerator<BenchParams>({
  id: "bench",
  label: "Bench",
  category: "prop",
  paramSpecs: BENCH_SPECS,
  defaultParams: paramsFromSpecs(BENCH_SPECS) as BenchParams,
  build: buildBench,
});
